import { useState, useEffect, useCallback } from "react";
import { getPlanningArchive } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send, ChevronDown, ChevronRight, Clock, Copy, ExternalLink, Mail, FileText, Loader2, RefreshCw, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";

// ISO-8601 week number from a YYYY-MM-DD date string
const getISOWeek = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return null;
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const ft = (firstThursday.getUTCDay() + 6) % 7;
  return 1 + Math.round(((d - firstThursday) / 86400000 - 3 + ft) / 7);
};

const PLAN_ACTION_META = {
  generated: { label: "Generated", Icon: FileText, chip: "bg-slate-100 text-slate-700 border-slate-300" },
  copied: { label: "Copied", Icon: Copy, chip: "bg-blue-100 text-blue-700 border-blue-300" },
  opened: { label: "Opened in mail app", Icon: ExternalLink, chip: "bg-amber-100 text-amber-700 border-amber-300" },
  sent: { label: "Sent from BSCA", Icon: Mail, chip: "bg-emerald-100 text-emerald-700 border-emerald-300" },
};

const MONTH_OPTIONS = [
  { v: "01", l: "January" }, { v: "02", l: "February" }, { v: "03", l: "March" },
  { v: "04", l: "April" }, { v: "05", l: "May" }, { v: "06", l: "June" },
  { v: "07", l: "July" }, { v: "08", l: "August" }, { v: "09", l: "September" },
  { v: "10", l: "October" }, { v: "11", l: "November" }, { v: "12", l: "December" },
];

export default function DailyPlanningsPage() {
  const [plannings, setPlannings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planYear, setPlanYear] = useState("all");
  const [planMonth, setPlanMonth] = useState("all");
  const [planWeek, setPlanWeek] = useState("all");
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [viewPlan, setViewPlan] = useState(null);

  const fetchPlannings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPlanningArchive();
      setPlannings(res.data.entries || []);
    } catch (error) {
      console.error("Failed to fetch planning archive:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlannings();
  }, [fetchPlannings]);

  const planYears = [...new Set(plannings.map((p) => (p.planning_date || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const planWeeks = [...new Set(plannings.map((p) => getISOWeek(p.planning_date)).filter(Boolean))].sort((a, b) => a - b);
  const filteredPlannings = plannings.filter((p) => {
    const date = p.planning_date || "";
    if (planYear !== "all" && date.slice(0, 4) !== planYear) return false;
    if (planMonth !== "all" && date.slice(5, 7) !== planMonth) return false;
    if (planWeek !== "all" && String(getISOWeek(date)) !== planWeek) return false;
    return true;
  });

  return (
    <div className="animate-fade-in" data-testid="daily-plannings-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] font-['Outfit']">Daily Plannings</h1>
          <p className="text-[#64748b] mt-1">Archive of tomorrow's plannings — generated, copied, opened or sent</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchPlannings}
          disabled={loading}
          className="border-[#e2e8f0]"
          data-testid="planning-refresh-btn"
        >
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Card className="border-[#e2e8f0] bg-white" data-testid="daily-plannings-card">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-[#0f172a] font-['Outfit']">
              <Send size={20} className="text-[#0b2545]" />
              Planning History
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={planYear} onValueChange={setPlanYear}>
                <SelectTrigger className="w-24 h-9 bg-white text-xs" data-testid="planning-filter-year">
                  <span className="text-[10px] text-[#64748b] mr-1">Year</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {planYears.map((y) => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={planMonth} onValueChange={setPlanMonth}>
                <SelectTrigger className="w-32 h-9 bg-white text-xs" data-testid="planning-filter-month">
                  <span className="text-[10px] text-[#64748b] mr-1">Month</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {MONTH_OPTIONS.map((m) => (
                    <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={planWeek} onValueChange={setPlanWeek}>
                <SelectTrigger className="w-28 h-9 bg-white text-xs" data-testid="planning-filter-week">
                  <span className="text-[10px] text-[#64748b] mr-1">Week</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {planWeeks.map((w) => (
                    <SelectItem key={w} value={String(w)}>W{w}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-[#64748b]">Last 2 events kept per day · click a row to see recipient emails</p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#64748b]">
              <Loader2 className="animate-spin mr-2" size={20} /> Loading plannings…
            </div>
          ) : filteredPlannings.length === 0 ? (
            <div className="p-3 text-sm text-[#94a3b8] italic bg-[#f8fafc] rounded-lg" data-testid="planning-archive-empty">
              No planning has been sent for this day.
            </div>
          ) : (
            <div className="space-y-2" data-testid="planning-archive-list">
              {filteredPlannings.map((p, idx) => {
                const meta = PLAN_ACTION_META[p.action] || PLAN_ACTION_META.generated;
                const { Icon } = meta;
                const key = `${p.planning_date}-${p.created_at}-${idx}`;
                const isOpen = expandedPlan === key;
                let dateLabel = p.planning_date;
                let hourLabel = "";
                try { dateLabel = format(parseISO(p.planning_date), "EEE dd MMM yyyy"); } catch (e) { /* keep raw */ }
                try { hourLabel = format(parseISO(p.created_at), "HH:mm"); } catch (e) { /* keep empty */ }
                return (
                  <div key={key} className="border border-[#e2e8f0] rounded-lg overflow-hidden" data-testid={`planning-row-${idx}`}>
                    <button
                      onClick={() => setExpandedPlan(isOpen ? null : key)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#f8fafc] transition-colors"
                      data-testid={`planning-row-toggle-${idx}`}
                    >
                      {isOpen ? <ChevronDown size={16} className="text-[#64748b] shrink-0" /> : <ChevronRight size={16} className="text-[#64748b] shrink-0" />}
                      <span className="text-sm font-semibold text-[#0f172a] shrink-0">{dateLabel}</span>
                      {hourLabel && (
                        <span className="text-xs text-[#64748b] flex items-center gap-1 shrink-0">
                          <Clock size={12} /> {hourLabel}
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full border ${meta.chip} shrink-0`}>
                        <Icon size={11} /> {meta.label}
                      </span>
                      <span className="text-xs text-[#64748b] ml-auto truncate">
                        by {p.actor_name || "—"}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-3 py-2.5 border-t border-[#e2e8f0] bg-[#f8fafc] text-sm" data-testid={`planning-row-detail-${idx}`}>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[#334155]">
                          <span><span className="text-[#64748b]">Flights:</span> {p.flight_count ?? "—"}</span>
                          <span><span className="text-[#64748b]">Sent by:</span> {p.actor_name || "—"}</span>
                        </div>
                        <div className="mt-2">
                          <span className="text-[#64748b] flex items-center gap-1 mb-1"><Mail size={13} /> Recipient emails:</span>
                          {p.recipients && p.recipients.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {p.recipients.map((r, ri) => (
                                <span key={ri} className="text-xs bg-white border border-[#e2e8f0] rounded px-2 py-0.5 text-[#0f172a]">{r}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-[#94a3b8] italic">No recipients (generated / copied in-app only)</span>
                          )}
                        </div>
                        {p.html ? (
                          <div className="mt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#0b2545] text-[#0b2545] hover:bg-[#0b2545] hover:text-white gap-1.5"
                              onClick={() => setViewPlan(p)}
                              data-testid={`planning-view-btn-${idx}`}
                            >
                              <Eye size={14} /> View planning that was sent
                            </Button>
                          </div>
                        ) : (
                          <div className="mt-3 text-xs text-[#94a3b8] italic">No saved copy for this event.</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Planning content viewer */}
      <Dialog open={!!viewPlan} onOpenChange={(o) => !o && setViewPlan(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="planning-view-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0b2545] dark:text-white flex items-center gap-2">
              <Send size={18} /> {viewPlan?.subject || "Planning"}
            </DialogTitle>
          </DialogHeader>
          <div
            className="border border-[#e2e8f0] rounded-lg overflow-auto bg-white p-2"
            style={{ maxHeight: "72vh" }}
            data-testid="planning-view-content"
            dangerouslySetInnerHTML={{ __html: viewPlan?.html || "" }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
